import { useEffect, useState } from "react";

import { useQuery, useQueryClient } from "@tanstack/react-query";
import ApiHttp from "byzantine/src/ApiHttp";

import { RECEIPTS_QUERY_KEY } from "../hooks/useReceiptsList";

import { resolvedStatusForReceiptStatus } from "./uploadSessionReducer";

import type { ReceiptListRow } from "../hooks/useReceiptsList";

export const IN_FLIGHT_QUERY_KEY = "inFlight";
export const IN_FLIGHT_POLL_INTERVAL_MS = 3_000;
export const IN_FLIGHT_POLL_SLOW_INTERVAL_MS = 30_000;
// Processing normally takes seconds, but the backend's own retries and the stuck-receipt sweeper can take much longer.
export const IN_FLIGHT_POLL_SLOW_AFTER_MS = 10 * 60 * 1_000;
export const IN_FLIGHT_POLL_GIVE_UP_MS = 60 * 60 * 1_000;
// The in-flight endpoint takes up to 100 uuids and returns them on one page.
export const IN_FLIGHT_POLL_LIMIT = 100;

export type InFlightReceiptsResponse = {
  receipts: ReceiptListRow[];
  meta: { total: number };
};

type UseInFlightReceiptStatusesOptions = {
  // Sorted so the query key is stable for the same set.
  receiptIds: string[];
  // Changes whenever the session adds or resolves something; resets the idle clock.
  activityCount: number;
  onResolved: (receiptId: string, receiptStatus: string) => void;
  // Called once when polling gives up with receipts still in flight.
  onStalled: () => void;
};

type PollPace = "fast" | "slow" | "stopped";

export const inFlightReceiptsPath = (receiptIds: string[]) => {
  const params = new URLSearchParams();
  receiptIds.forEach((receiptId) => params.append("uuid", receiptId));
  return `receipts/in-flight/?${params.toString()}`;
};

export const inFlightReceiptsPaths = (receiptIds: string[]) => {
  const paths: string[] = [];
  for (
    let start = 0;
    start < receiptIds.length;
    start += IN_FLIGHT_POLL_LIMIT
  ) {
    paths.push(
      inFlightReceiptsPath(
        receiptIds.slice(start, start + IN_FLIGHT_POLL_LIMIT)
      )
    );
  }
  return paths;
};

const fetchInFlightReceipts = async (
  receiptIds: string[]
): Promise<InFlightReceiptsResponse> => {
  const pages = await Promise.all(
    inFlightReceiptsPaths(receiptIds).map((path) =>
      ApiHttp.fetch<InFlightReceiptsResponse>(path)
    )
  );
  return {
    receipts: pages.flatMap((page) => page.receipts),
    meta: { total: pages.reduce((sum, page) => sum + page.meta.total, 0) },
  };
};

export const useInFlightReceiptStatuses = ({
  receiptIds,
  activityCount,
  onResolved,
  onStalled,
}: UseInFlightReceiptStatusesOptions) => {
  const queryClient = useQueryClient();
  const [pace, setPace] = useState<PollPace>("fast");
  const hasReceipts = receiptIds.length > 0;

  useEffect(() => {
    setPace("fast");
    if (!hasReceipts) return undefined;
    const slowHandle = setTimeout(
      () => setPace("slow"),
      IN_FLIGHT_POLL_SLOW_AFTER_MS
    );
    const stopHandle = setTimeout(
      () => setPace("stopped"),
      IN_FLIGHT_POLL_GIVE_UP_MS
    );
    return () => {
      clearTimeout(slowHandle);
      clearTimeout(stopHandle);
    };
  }, [activityCount, hasReceipts]);

  useEffect(() => {
    if (pace === "stopped" && hasReceipts) onStalled();
  }, [pace, hasReceipts, onStalled]);

  const enabled = hasReceipts && pace !== "stopped";
  const interval =
    pace === "slow"
      ? IN_FLIGHT_POLL_SLOW_INTERVAL_MS
      : IN_FLIGHT_POLL_INTERVAL_MS;

  const { data } = useQuery<InFlightReceiptsResponse>({
    queryKey: [RECEIPTS_QUERY_KEY, IN_FLIGHT_QUERY_KEY, receiptIds],
    queryFn: () => fetchInFlightReceipts(receiptIds),
    enabled,
    refetchInterval: enabled ? interval : false,
    retry: false,
  });

  useEffect(() => {
    if (!data) return;
    const resolvedRows = data.receipts.filter((row) =>
      Boolean(resolvedStatusForReceiptStatus(row.status))
    );
    if (resolvedRows.length === 0) return;
    resolvedRows.forEach((row) => onResolved(row.uuid, row.status));
    queryClient.invalidateQueries({
      predicate: (query) =>
        query.queryKey[0] === RECEIPTS_QUERY_KEY &&
        query.queryKey[1] !== IN_FLIGHT_QUERY_KEY,
    });
  }, [data, onResolved, queryClient]);

  return { isPolling: enabled, pace };
};
